import React, { useEffect, useState } from 'react';
import { Button, Form, Spinner, Alert, Modal, ListGroup, InputGroup, Badge } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { cookie } from 'utils/cookies';

import {
    FETCH_STATION_WISE_FORMS,
    DOWNLOAD_CHECKLIST_FORM,
    STATUS_CHANGE,
    CREATE_CHECKLIST,
    DELETE_CHECKLIST_FORM,
    ASSIGN_TO_CONTRACTOR, // ✅ NEW
} from 'actions/formActions';
import { FaDownload, FaPen, FaSpinner, FaCheck, FaTimes, FaTrash, FaUserPlus, FaInfoCircle, FaSearch } from 'react-icons/fa';
import { FETCH_USERS } from 'actions/userActions';


const StationForms = () => {
    const { stationId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {
        loading,
        stationwiseformslist = [],
        downloadLoading,
        error,
        statusLoading,
        deleteLoading,
    } = useSelector((state) => state.checklist);

    const { users } = useSelector((state) => state.users);

    const contractors =
        Array.isArray(users)
            ? users.filter((u) => (u?.role || '').toLowerCase() === 'contractor')
            : [];


    const [search, setSearch] = useState('');
    const [formFilter, setFormFilter] = useState('');
    const [downloadInProgress, setDownloadInProgress] = useState(null);
    const [statusChangeInProgress, setStatusChangeInProgress] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [selectedFormId, setSelectedFormId] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [formName, setFormName] = useState('');
    const [showAddFormsModal, setShowAddFormsModal] = useState(false);
    const [quantities, setQuantities] = useState({});
    const [addFormError, setAddFormError] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteInProgress, setDeleteInProgress] = useState(null);

    // ✅ Contractor modal state (RESTORING)
    const [showContractorModal, setShowContractorModal] = useState(false);
    const [contractorLevel, setContractorLevel] = useState(null);
    const [selectedContractorId, setSelectedContractorId] = useState('');
    const [assignInProgress, setAssignInProgress] = useState(false);
    const [isSearchExpanded, setIsSearchExpanded] = useState(false); // ✅ NEW

    const clearFilters = () => {
        setSearch('');
        setFormFilter('');
        setStatusFilter('');
        setStartDate('');
        setEndDate('');
    };

    // RESTORED contractor functions
    const openContractorModal = (form, level /* 'l1' | 'l2' | 'root' */) => {
        setSelectedFormId(form._id);
        setFormName(form.formName);
        setContractorLevel(level);
        setSelectedContractorId('');
        setShowContractorModal(true);

        dispatch({
            type: FETCH_USERS,
            payload: { role: 'contractor', limit: 100, page: 1 },
        });
    };

    const handleAssignToContractor = () => {
        if (!selectedContractorId) {
            toast.error('Please select a contractor');
            return;
        }
        setAssignInProgress(true);
        dispatch({
            type: ASSIGN_TO_CONTRACTOR,
            payload: {
                formId: selectedFormId,
                formName,
                level: contractorLevel, // 'l1' | 'l2' | 'root'
                contractorId: selectedContractorId,
                stationId,
            },
            cb: (res) => {
                setAssignInProgress(false);
                if (res?.status === 200) {
                    toast.success('Assigned to contractor');
                    setShowContractorModal(false);
                    fetchStationWiseForms();
                } else {
                    toast.error(res?.data?.message || 'Failed to assign');
                }
            },
        });
    };


    const userRole = cookie.get('user-role');

    const formNameToComponent = {
        db_panel: 'DBPanelForm',
        apfc_panel: 'APFCPanelForm',
        cabel_tray: 'CabelTrayForm',
        earthing: 'EarthingForm',
        ht_cable: 'HTCableForm',
        lightning_arrester: 'LightningArresterForm',
        lt_cable: 'LTCableForm',
        lt_panel: 'LTPanelForm',
        transformer: 'TransformerForm',
        vcb: 'VCBForm',
        hoto: 'HOTOForm',
    };

    const checklistItems = [
        'APFC Panel',
        'Cabel Tray',
        'DB Panel',
        'Earthing',
        'HT Cabel',
        'Lightning Arrester',
        'LT Cabel',
        'LT Panel',
        'Transformer',
        'VCB',
        'HOTO (Project Handover Takeover Report)',
    ];

    const formNames = Object.keys(formNameToComponent);

    const prettifyFormName = (name = '') =>
        String(name).replace(/\(.*?\)/g, '').replace(/_/g, ' ').trim().toUpperCase();

    const statusLabel = (status = '', form = null, level = null) => {
        const s = (status || '').toLowerCase();
        switch (s) {
            case 'approved':
                return 'APPROVED';
            case 'rejected':
                return 'REJECTED';
            case 're-work':
            case 'rework':
                return 'RE-WORK';
            case 'submitted':
            case 'draft':
                return 'DRAFT';
            case 'contractor':
                return 'PENDING FROM CONTRACTOR';
            case 'pending':
                if (level === 'l1') return 'PENDING FROM MANAGER';
                if (level === 'l2') return 'PENDING FROM QUALITY';
                // Global inference
                if (form) {
                    if (form.l2 && (form.l2.status || '').toLowerCase() === 'pending') return 'PENDING FROM QUALITY';
                    if (form.l1 && (form.l1.status || '').toLowerCase() === 'pending') return 'PENDING FROM MANAGER';
                    if ((form.l1?.status || '').toLowerCase() === 'contractor' || (form.l2?.status || '').toLowerCase() === 'contractor') return 'PENDING FROM CONTRACTOR';
                }
                return 'PENDING';
            default:
                return (status || 'N/A').toUpperCase();
        }
    };


    const fetchStationWiseForms = () => {
        if (stationId) {
            dispatch({ type: FETCH_STATION_WISE_FORMS, payload: stationId });
        }
    };

    useEffect(() => {
        fetchStationWiseForms();
        setQuantities(checklistItems.reduce((acc, item) => ({ ...acc, [item]: 0 }), {}));
    }, [dispatch, stationId, statusLoading]); // keep your existing deps

    const initiateStatusChange = (formId, formName, newStatus) => {
        setSelectedFormId(formId);
        setSelectedStatus(newStatus);
        setFormName(formName);
        setShowStatusModal(true);
    };

    const handleStatusChange = () => {
        if (selectedFormId) {
            setStatusChangeInProgress(selectedFormId);
            dispatch({
                type: STATUS_CHANGE,
                payload: { formId: selectedFormId, status: selectedStatus, formName },
            });
            if (statusLoading) {
                setStatusChangeInProgress(null);
                toast.success(`Form status changed to ${selectedStatus}`);
                setShowStatusModal(false);
            }
        }
    };

    const initiateDeleteForm = (formId, formName) => {
        setSelectedFormId(formId);
        setFormName(formName);
        setShowDeleteModal(true);
    };

    const handleDeleteForm = () => {
        if (selectedFormId) {
            setDeleteInProgress(selectedFormId);
            dispatch({
                type: DELETE_CHECKLIST_FORM,
                payload: { formId: selectedFormId, formName },
            });
            if (!deleteLoading) {
                fetchStationWiseForms({});
                setShowDeleteModal(false);
            }
        }
    };

    const handleCloseStatusModal = () => {
        setShowStatusModal(false);
        setSelectedFormId(null);
        setSelectedStatus('');
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setSelectedFormId(null);
        setFormName('');
    };

    const handleAddFormsModalOpen = () => {
        setShowAddFormsModal(true);
        setAddFormError('');
    };

    const handleAddFormsModalClose = () => {
        setShowAddFormsModal(false);
        setQuantities(checklistItems.reduce((acc, item) => ({ ...acc, [item]: 0 }), {}));
        setAddFormError('');
    };

    const handleQuantityChange = (item, value) => {
        const qty = parseInt(value, 10) || 0;
        setQuantities((prev) => ({ ...prev, [item]: qty }));
        if (addFormError) setAddFormError('');
    };

    const adjustQuantity = (item, delta) => {
        setQuantities((prev) => {
            const updated = Math.max(0, (prev[item] || 0) + delta);
            return { ...prev, [item]: updated };
        });
    };

    const validateAddForms = () => {
        const totalSelected = Object.values(quantities).reduce((sum, qty) => sum + (qty > 0 ? 1 : 0), 0);
        if (totalSelected === 0) {
            setAddFormError('Select at least one form with quantity greater than 0.');
            return false;
        }
        return true;
    };

    const handleAddFormsSubmit = () => {
        if (!validateAddForms()) return;

        const checklist = {};
        Object.entries(quantities).forEach(([item, quantity]) => {
            if (quantity > 0) {
                const key = item
                    .toLowerCase()
                    .replace(/\(.*?\)/g, '')
                    .replace(/[^a-z0-9]+/g, '_')
                    .replace(/^_|_$/g, '');
                checklist[key] = quantity;
            }
        });

        const payload = {
            stationId: stationId.toUpperCase(),
            project_name: stationwiseformslist[0]?.project_name || 'Unknown',
            checklist,
            is_existing_station: true,
        };

        dispatch({
            type: CREATE_CHECKLIST,
            payload,
            cb: (res) => {
                if (res.status === 200) {
                    toast.success('Forms added successfully');
                    fetchStationWiseForms();
                    handleAddFormsModalClose();
                } else {
                    setAddFormError(res.data?.message || 'Failed to add forms');
                }
            },
        });
    };

    useEffect(() => {
        if (!downloadLoading) {
            setDownloadInProgress(null);
        }
    }, [downloadLoading]);

    const filteredForms = Array.isArray(stationwiseformslist)
        ? stationwiseformslist.filter((f) => {
            if (!f || typeof f !== 'object') return false;

            const formNameMatch =
                f.formName?.toLowerCase().includes(search.toLowerCase()) ||
                f.userName?.toLowerCase().includes(search.toLowerCase());

            const formFilterMatch = !formFilter || f.formName?.toLowerCase() === formFilter.toLowerCase();

            const statusMatch = !statusFilter || (f.status || '').toLowerCase() === statusFilter.toLowerCase();

            const formDate = new Date(f.createdAt);
            const start = startDate ? new Date(startDate) : null;
            const end = endDate ? new Date(endDate) : null;

            const dateInRange =
                (!start || formDate >= start) && (!end || formDate <= new Date(end.setHours(23, 59, 59, 999)));

            return formNameMatch && formFilterMatch && statusMatch && dateInRange;
        })
        : [];

    const sortedForms = [...filteredForms].sort((a, b) => {
        const aTime = a?.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const bTime = b?.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return bTime - aTime;
    });

    const handleDownload = (formId, formName, userName) => {
        if (formId) {
            setDownloadInProgress(formId);
            dispatch({
                type: DOWNLOAD_CHECKLIST_FORM,
                payload: { formId, formName, userName, stationId },
                meta: { formId },
            });
        }
    };

    const formatToIST = (utcDate) => {
        if (!utcDate) return 'N/A';
        const date = new Date(utcDate);
        return date.toLocaleString('en-IN', {
            timeZone: 'Asia/Kolkata',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        });
    };




    return (
        <div className="page-body checklist-container">
            <div className="container-xl">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2 className="page-title m-0">Station: {stationId}</h2>
                    <Button variant="primary" className="btn-premium-wide" onClick={handleAddFormsModalOpen} style={{ borderRadius: '10px', padding: '0.6rem 1.5rem', background: '#1abc9c', border: 'none' }}>
                        + Add Forms
                    </Button>
                </div>

                {error && (
                    <Alert variant="danger" className="mb-4">Error fetching forms: {error.message || 'Unknown error'}</Alert>
                )}

                <div className="premium-filter-section mb-4">
                    <div className={`filter-group search-wrapper ${search || isSearchExpanded ? 'expanded' : ''}`}>
                        <label htmlFor="form-search">Search</label>
                        <div className="position-relative">
                            <Form.Control
                                id="form-search"
                                type="text"
                                placeholder="Search User or Form..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onFocus={() => setIsSearchExpanded(true)}
                                onBlur={() => !search && setIsSearchExpanded(false)}
                            />
                            <FaSearch className="search-icon" />
                        </div>
                    </div>

                    <div className="filter-group">
                        <label htmlFor="form-type-filter">Form Type</label>
                        <Form.Select 
                            id="form-type-filter"
                            value={formFilter} 
                            onChange={(e) => setFormFilter(e.target.value)}
                        >
                            <option value="">All Form Types</option>
                            {formNames.map((name, idx) => (
                                <option key={idx} value={name}>
                                    {prettifyFormName(name)}
                                </option>
                            ))}
                        </Form.Select>
                    </div>

                    <div className="filter-group">
                        <label htmlFor="status-filter">Status</label>
                        <Form.Select 
                            id="status-filter"
                            value={statusFilter} 
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="">All Status</option>
                            <option value="pending">Pending</option>
                            <option value="submitted">Draft</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                            <option value="re-work">Re-Work</option>
                            <option value="contractor">Contractor</option>
                        </Form.Select>
                    </div>

                    <div className="filter-group">
                        <label htmlFor="start-date-filter">From Date</label>
                        <Form.Control 
                            id="start-date-filter"
                            type="date" 
                            value={startDate} 
                            onChange={(e) => setStartDate(e.target.value)} 
                        />
                    </div>

                    <div className="filter-group">
                        <label htmlFor="end-date-filter">To Date</label>
                        <Form.Control 
                            id="end-date-filter"
                            type="date" 
                            value={endDate} 
                            onChange={(e) => setEndDate(e.target.value)} 
                        />
                    </div>

                    {(search || formFilter || statusFilter || startDate || endDate) && (
                        <div className="filter-group flex-grow-0">
                            <span className="filter-label-spacer" aria-hidden="true">&nbsp;</span>
                            <button 
                                id="clear-all-filters-btn"
                                className="btn-clear" 
                                onClick={clearFilters} 
                                title="Clear All Filters"
                            >
                                <FaTimes /> Clear
                            </button>
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="text-center my-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : (
                    <div className="table-responsive">
                    <table className="premium-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Form Name</th>
                                <th>Ver.</th>
                                <th>User</th>
                                <th>Global Status</th>
                                <th>L1 Status</th>
                                <th>L2 Status</th>
                                <th>Updated</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedForms.length > 0 ? (
                                sortedForms.map((form) => (
                                    <tr key={form._id}>
                                        <td className="text-uppercase small fw-bold">#{form?._id?.slice(-6)}</td>
                                        <td className="text-uppercase fw-bold" style={{ fontSize: '0.85rem' }}>{prettifyFormName(form?.formName || 'N/A')}</td>
                                        <td><Badge bg="light" className="text-dark border">{form?.version || '1'}</Badge></td>
                                        <td><span className="text-muted small">{form?.userName || 'N/A'}</span></td>
                                        <td>
                                            <span className={`status-pill status-${(form.status || '').toLowerCase()}`}>
                                                {statusLabel(form.status, form)}
                                            </span>
                                        </td>

                                        <td>{form.l1 ? (
                                            <div className="d-flex align-items-center gap-2">
                                                <span className={`status-pill status-${(form.l1.status || '').toLowerCase()}`}>
                                                    {statusLabel(form.l1.status, form, 'l1')}
                                                </span>
                                                {userRole === 'engineer' && (form.l1.status || '').toLowerCase() === 'contractor' && (
                                                    <button 
                                                        className="btn-link p-0 border-0 bg-transparent text-primary" 
                                                        title={form.l1.contractorId ? "View Contractor" : "Assign Contractor"}
                                                        onClick={() => openContractorModal(form, 'l1')}
                                                    >
                                                        {form.l1.contractorId ? <FaInfoCircle size={14} /> : <FaUserPlus size={14} />}
                                                    </button>
                                                )}
                                            </div>
                                        ) : <span className="text-muted small">N/A</span>}</td>
                                        <td>{form.l2 ? (
                                            <div className="d-flex align-items-center gap-2">
                                                <span className={`status-pill status-${(form.l2.status || '').toLowerCase()}`}>
                                                    {statusLabel(form.l2.status, form, 'l2')}
                                                </span>
                                                {userRole === 'engineer' && (form.l2.status || '').toLowerCase() === 'contractor' && (
                                                    <button 
                                                        className="btn-link p-0 border-0 bg-transparent text-primary" 
                                                        title={form.l2.contractorId ? "View Contractor" : "Assign Contractor"}
                                                        onClick={() => openContractorModal(form, 'l2')}
                                                    >
                                                        {form.l2.contractorId ? <FaInfoCircle size={14} /> : <FaUserPlus size={14} />}
                                                    </button>
                                                )}
                                            </div>
                                        ) : <span className="text-muted small">N/A</span>}</td>

                                        <td className="small text-muted">{formatToIST(form?.updatedAt)}</td>
                                        <td>
                                            <div className="action-btn-group">
                                                {form?.status !== 'approved' && form?.status !== 'rejected' && (
                                                    <button
                                                        className="btn-premium"
                                                        title="Edit Form"
                                                        onClick={() =>
                                                            navigate(`/formList/${stationId}/${form?.formName}/${form._id}`)
                                                        }
                                                        disabled={!form._id}
                                                    >
                                                        <FaPen size={14} />
                                                    </button>
                                                )}

                                                {userRole === 'engineer' && form?.status === 'pending' && (
                                                    <>
                                                        <button
                                                            className="btn-premium text-success"
                                                            title="Approve"
                                                            onClick={() => initiateStatusChange(form._id, form.formName, 'approved')}
                                                        >
                                                            <FaCheck />
                                                        </button>
                                                        <button
                                                            className="btn-premium text-danger"
                                                            title="Reject"
                                                            onClick={() => initiateStatusChange(form._id, form.formName, 'rejected')}
                                                        >
                                                            <FaTimes />
                                                        </button>
                                                    </>
                                                )}
                                                
                                                <button
                                                    className="btn-premium"
                                                    title="Download PDF"
                                                    onClick={() => handleDownload(form._id, form?.formName, form?.userName)}
                                                    disabled={!form._id || downloadInProgress === form._id}
                                                >
                                                    {downloadInProgress === form._id ? <FaSpinner className="animate-spin" size={14} /> : <FaDownload size={14} />}
                                                </button>

                                                {userRole === 'engineer' && (
                                                    <button
                                                        className="btn-premium btn-delete"
                                                        title="Delete Form"
                                                        onClick={() => initiateDeleteForm(form._id, form.formName)}
                                                        disabled={deleteInProgress === form._id}
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="9" className="text-center py-5 text-muted">
                                        No forms available for this station.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    </div>
                )}

                {/* Status Change Confirmation Modal */}
                <Modal show={showStatusModal} onHide={handleCloseStatusModal} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Status Change</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>Are you sure you want to {statusLabel(selectedStatus)} this form?</Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseStatusModal}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleStatusChange}
                            disabled={statusChangeInProgress === selectedFormId}
                        >
                            {statusChangeInProgress === selectedFormId ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="me-2"
                                    />
                                    Changing...
                                </>
                            ) : (
                                'Confirm'
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Delete</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>{`Are you sure you want to delete the form "${formName}"?`}</Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseDeleteModal}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={handleDeleteForm} disabled={deleteInProgress === selectedFormId}>
                            {deleteInProgress === selectedFormId ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="me-2"
                                    />
                                    Deleting...
                                </>
                            ) : (
                                'Delete'
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* ✅ Send to Contractor Modal (RESTORING) */}
                <Modal show={showContractorModal} onHide={() => setShowContractorModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Contractor Assignment</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="mb-2">
                            <strong>Form:</strong> {prettifyFormName(formName)} &nbsp;|&nbsp; <strong>Level:</strong>{' '}
                            {contractorLevel === 'root' ? 'ROOT' : String(contractorLevel || '').toUpperCase()}
                        </div>
                        {(() => {
                            // Find the current form
                            const currentForm = stationwiseformslist.find(f => f._id === selectedFormId);
                            if (!currentForm) return <div>Form not found</div>;

                            const levelData = contractorLevel === 'root' ? currentForm : currentForm[contractorLevel];
                            const isAssigned = levelData?.contractorId;
                            const assignedContractor = isAssigned ? contractors.find(c => (c.id || c._id) === levelData.contractorId) : null;
                            const assignedAt = levelData?.contractorAssignedAt ? formatToIST(levelData.contractorAssignedAt) : null;

                            if (isAssigned && assignedContractor) {
                                return (
                                    <div className="premium-card p-3 border-0" style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
                                        <h5 className="mb-3" style={{ color: '#1abc9c' }}>Assigned Contractor</h5>
                                        <div className="d-flex align-items-center mb-3">
                                            <div className="avatar me-3 bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                                                {assignedContractor.name?.charAt(0) || 'C'}
                                            </div>
                                            <div>
                                                <div className="fw-bold">{assignedContractor.name || `${assignedContractor.firstName || ''} ${assignedContractor.lastName || ''}`.trim()}</div>
                                                <div className="small text-muted">{assignedContractor.email}</div>
                                            </div>
                                        </div>
                                        {assignedAt && (
                                            <div className="small border-top pt-2 mt-2">
                                                <span className="text-muted">Assigned On:</span> <span className="fw-bold">{assignedAt}</span>
                                            </div>
                                        )}
                                    </div>
                                );
                            } else {
                                return (
                                    <Form.Group>
                                        <Form.Label>Select Contractor</Form.Label>
                                        <Form.Select
                                            value={selectedContractorId}
                                            onChange={(e) => setSelectedContractorId(e.target.value)}
                                        >
                                            <option value="">Choose a contractor</option>
                                            {contractors.map((u) => (
                                                <option key={u.id || u._id} value={u.id || u._id}>
                                                    {u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim()} ({u.email})
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Text className="text-muted small mt-2 d-block">
                                            Only users with the <strong>Contractor</strong> role are listed here.
                                        </Form.Text>
                                    </Form.Group>
                                );
                            }
                        })()}
                    </Modal.Body>
                    <Modal.Footer>
                        {(() => {
                            const currentForm = stationwiseformslist.find(f => f._id === selectedFormId);
                            const levelData = contractorLevel === 'root' ? currentForm : currentForm?.[contractorLevel];
                            const isAssigned = levelData?.contractorId;

                            if (!isAssigned) {
                                return (
                                    <>
                                        <Button variant="secondary" onClick={() => setShowContractorModal(false)}>
                                            Cancel
                                        </Button>
                                        <Button 
                                            variant="primary" 
                                            onClick={handleAssignToContractor} 
                                            disabled={assignInProgress || !selectedContractorId}
                                            style={{ background: '#1abc9c', border: 'none' }}
                                        >
                                            {assignInProgress ? (
                                                <><FaSpinner className="animate-spin me-2" /> Sending...</>
                                            ) : (
                                                'Send Assignment'
                                            )}
                                        </Button>
                                    </>
                                );
                            } else {
                                return (
                                    <Button variant="secondary" onClick={() => setShowContractorModal(false)}>
                                        Close
                                    </Button>
                                );
                            }
                        })()}
                    </Modal.Footer>
                </Modal>


                {/* Add Forms Modal (unchanged) */}
                <Modal show={showAddFormsModal} onHide={handleAddFormsModalClose} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Add Forms to Station: {stationId}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {addFormError && (
                            <Alert variant="danger" className="mb-3">
                                {addFormError}
                            </Alert>
                        )}
                        <ListGroup>
                            {checklistItems.map((item) => (
                                <ListGroup.Item key={item} className="d-flex align-items-center">
                                    <span className="flex-grow-1">{item}</span>
                                    <InputGroup style={{ width: '150px' }}>
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => adjustQuantity(item, -1)}
                                            disabled={quantities[item] === 0}
                                        >
                                            −
                                        </Button>
                                        <Form.Control
                                            type="number"
                                            value={quantities[item]}
                                            min={0}
                                            onChange={(e) => handleQuantityChange(item, e.target.value)}
                                            style={{ textAlign: 'center' }}
                                        />
                                        <Button variant="outline-secondary" onClick={() => adjustQuantity(item, 1)}>
                                            +
                                        </Button>
                                    </InputGroup>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleAddFormsModalClose}>
                            Cancel
                        </Button>
                        <Button variant="success" onClick={handleAddFormsSubmit}>
                            Send
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    );
};

export default StationForms;
